import type { TFunction } from "i18next";
import type { DeepReadonly, ValueOf } from "ts-essentials";

import router, { setRoute } from "@/__main__/router.mjs";
import { appURLs } from "@/app/constants.mjs";
import type {
  MatchFull,
  MatchStart,
  PlayerMatchRoundStat,
} from "@/data-models/csgo-game-events.mjs";
import type { GameMode } from "@/game-csgo/constants.mjs";
import {
  BOT_STEAM_ID,
  GAME_MODE_RANKS,
  GAME_MODES,
  gameModes,
  RANKS,
  ROUND_END_RESULT,
  TEAM_IDS,
} from "@/game-csgo/constants.mjs";
import * as cs2Utils from "@/game-csgo/utils-cs2.mjs";
import CSGOCounterTerroristIcon from "@/inline-assets/csgo-counter-terrorist-icon.webp";
import CSGOTerroristIcon from "@/inline-assets/csgo-terrorist-icon.webp";
import { appLog, appWarn } from "@/util/dev.mjs";
import { calcRate } from "@/util/helpers.mjs";
import isRouteOverlay from "@/util/is-route-overlay.mjs";
import orderArrayBy from "@/util/order-array-by.mjs";
import rangeBucket from "@/util/range-bucket.mjs";

export * from "@/game-csgo/utils-cs2.mjs";

export const getGameModeByMatch = (
  match: DeepReadonly<MatchFull | MatchStart>,
): GameMode | undefined => {
  if (!match) return;
  const identifier = `${match.gameType}_${match.gameMode}`;
  return GAME_MODES[identifier];
};

export function getGameModeById(gameModeId: GameMode["id"] | string): GameMode {
  return (
    (gameModeId && gameModes.find((i) => i.id === gameModeId)) || GAME_MODES.ALL
  );
}

const gameModeParams = ["gameType", "gameMode", "maxRounds"];

export const getModeParams = (
  gameModeId?: GameMode["id"],
): { gameType?: number; gameMode?: number } => {
  if (!gameModeId || gameModeId === GAME_MODES.ALL.id) return {};
  const gameModeKey = gameModes.find((i) => i.id === gameModeId)?.key;
  if (!gameModeKey) return;
  return gameModeKey.split("_").reduce((result, param, i) => {
    const value = Number(param);
    if (typeof value === "number") result[gameModeParams[i]] = value;
    return result;
  }, {});
};

const winRateBucket = rangeBucket([
  [0, "var(--perf-neg3)"],
  [46, "var(--perf-neg2)"],
  [48, "var(--perf-neg1)"],
  [50, "var(--perf-neutral)"],
  [51, "var(--perf-pos1)"],
  [53, "var(--perf-pos2)"],
  [55, "var(--perf-pos3)"],
]);
export const getWinRateColor = (rate: number) =>
  typeof rate === "number" ? winRateBucket[rate * 100] : "var(--shade3)";

const headShotBucket = rangeBucket([
  [0, "var(--perf-neg3)"],
  [9, "var(--perf-neg2)"],
  [17, "var(--perf-neg1)"],
  [25, "var(--perf-neutral)"],
  [33, "#DEAF78"],
  [41, "#E6A85F"],
  [49, "#FF9417"],
  [57, "#ffbe05"],
]);

export const getHeadshotColor = (percent: number) =>
  headShotBucket[percent] || "var(--shade3)";

const averageDamageBucket = rangeBucket([
  [0, "var(--perf-neg3)"],
  [31, "var(--perf-neg2)"],
  [41, "var(--perf-neg1)"],
  [50, "var(--perf-neutral)"],
  [71, "var(--perf-pos1)"],
  [81, "var(--perf-pos2)"],
  [91, "var(--perf-pos3)"],
]);

export const getAverageDamageColor = (adr: number) => averageDamageBucket[adr];

export function getGameModeTranslation(t: TFunction, mode: string) {
  switch (mode) {
    case "competitive":
      return t("common:csgo.gameMode.competitive", "Competitive");
    case "casual":
      return t("common:csgo.gameMode.casual", "Casual");
    case "deathmatch":
      return t("common:csgo.gameMode.deathmatch", "Deathmatch");
    case "armsRace":
      return t("common:csgo.gameMode.arms_race", "Arms Race");
    case "demolition":
      return t("common:csgo.gameMode.demolition", "Demolition");
    case "wingman":
      return t("common:csgo.gameMode.wingman", "Wingman");
    case "flyingScoutsman":
      return t("common:csgo.gameMode.flying_scoutsman", "Flying Scoutsman");
    case "retakes":
      return t("common:csgo.gameMode.retakes", "Retakes ");
    case "dangerZone":
      return t("common:csgo.gameMode.danger_zone", "Danger Zone");
    default:
      break;
  }
}

export const getMapPrefix = (map: string): string | undefined =>
  typeof map === "string" && map.length
    ? (map.match(/(.*)_/) || [])[1]
    : undefined;

export const getHitZone = (hitBone: string[]) => {
  if (hitBone.includes("head")) return "headshots";
  if (
    hitBone.includes("chest") ||
    hitBone.includes("arm") ||
    hitBone.includes("spine") ||
    hitBone.includes("neck")
  )
    return "chestshots";
  if (hitBone.includes("pelvis")) return "stomachshots";
  if (hitBone.includes("leg")) return "legshots";
  return null;
};

export type ScoreCard = PlayerMatchRoundStat & {
  hs: number;
  adr: number;
  kda: number;
  isUser: boolean;
};

// This seems like it will be reused for Benchmarking overlay if its enabled
export const getScoreCardTeams = (
  match: DeepReadonly<MatchFull>,
  playerSteamId: string,
): [ScoreCard[], ScoreCard[]] => {
  const roundLength = cs2Utils.getRoundsLength(match.rounds);
  const lastRound = cs2Utils.getLastRound(match);
  const isDM = cs2Utils.isDeathMatch(match);
  const players = getSortedPlayersByLastRound(lastRound?.playerMatchRoundStats);
  const currentPlayer = players.find((i) => i.steamId === playerSteamId);
  const currentPlayerTeamId = currentPlayer?.teamId;
  // Declare
  const terrorist: ScoreCard[] = []; // If match is Deathmatch game mode, then this is used for all players
  const counterTerrorist: ScoreCard[] = [];
  // Populate
  for (const player of players) {
    // Does player belong to a valid team
    if (!Object.values(TEAM_IDS).some((i) => i === player.teamId)) continue;
    // Calc
    const hs = calcRate(player.hsKills, player.kills);
    const adr = calcRate(player.damage, roundLength, 0);
    const kda = calcRate(player.kills + player.assists, player.deaths);
    const playerInfo: ScoreCard = {
      ...player,
      hs,
      adr,
      kda,
      isUser: player.steamId === playerSteamId,
    };
    // Populate T or CT team
    if (player.teamId === TEAM_IDS.CT) {
      counterTerrorist.push(playerInfo);
    } else if (player.teamId === TEAM_IDS.T || isDM) {
      // If Deathmatch, everyone joins T
      terrorist.push(playerInfo);
    }
  }
  // Sort
  orderArrayBy(terrorist, ["score", "kills"], ["desc", "desc"]);
  orderArrayBy(counterTerrorist, ["score", "kills"], ["desc", "desc"]);

  return isDM || currentPlayerTeamId === TEAM_IDS.T
    ? [terrorist, counterTerrorist]
    : [counterTerrorist, terrorist];
};

export const getRoundWinner = (
  roundEndResult: ValueOf<typeof ROUND_END_RESULT>,
) => {
  switch (roundEndResult) {
    case ROUND_END_RESULT.T_WIN_ELIMINATION:
    case ROUND_END_RESULT.T_WIN_BOMB:
    case ROUND_END_RESULT.T_WIN_TIME:
      return TEAM_IDS.T;
    case ROUND_END_RESULT.CT_WIN_ELIMINATION:
    case ROUND_END_RESULT.CT_WIN_RESCUE:
    case ROUND_END_RESULT.CT_WIN_DEFUSE:
    case ROUND_END_RESULT.CT_WIN_TIME:
      return TEAM_IDS.CT;
    default:
      return 0;
  }
};

export const getSortedPlayersByLastRound = (
  players: DeepReadonly<PlayerMatchRoundStat[]>,
): PlayerMatchRoundStat[] => {
  return orderArrayBy(players?.slice() ?? [], "score", "desc");
};

export const createPlayerMatchStats = (match: DeepReadonly<MatchFull>) => {
  // This is for RootMutationType.updatePostMatch transformation
  const playerMatchStats = [];
  const lastRound = cs2Utils.getLastRound(match);
  if (lastRound?.playerMatchRoundStats) {
    const playerStats: PlayerMatchRoundStat[] = orderArrayBy(
      // Duplicate STEAM_ID throw errors
      lastRound.playerMatchRoundStats.filter((i) => i.steamId !== BOT_STEAM_ID),
      "score",
      "desc",
    );
    const tScore = lastRound.terroristCurrentScore;
    const ctScore = lastRound.counterTerroristCurrentScore;
    // A draw is the same for both sides, although this doesn't seem possible with CS2, a winner is always declared?
    const draw = Number(ctScore === tScore);
    for (let i = 0; i < playerStats.length; i += 1) {
      const playerStat = playerStats[i];
      const rank = i + 1;
      const wonLost = cs2Utils.getWonLost({
        player: playerStat,
        round: lastRound,
      });
      // Player match stat
      const playerMatchStat = {
        // The only things that doesn't need calculations, woweeeee
        steamId: playerStat.steamId,
        username: playerStat.username,
        // Calculations
        draw, // This has to be either 1 or 0 not true or false
        rank,
        ...wonLost,
        placement: cs2Utils.getPlayerPlacement({
          wonLost,
          match,
          rank,
        }),
        // This will crash the mutation and return a server error, wtf?
        // isValveDedicated: match.isValveDedicated,
      };
      playerMatchStats.push(playerMatchStat);
    }
  }
  return playerMatchStats;
};

export const navigateToInGame = () => {
  // Client flow only, redirect user to cs2/in-game
  const route = "/cs2/in-game";
  if (router.route?.currentPath === route || isRouteOverlay()) return;
  appLog("[CS2] Navigating to in-game");
  setRoute(route);
};

export const getMatchStats = (
  player: PlayerMatchRoundStat,
  roundsLength: number,
): {
  kpr: number;
  kda: number;
  dpr: number;
} => {
  // Constants
  const { kills = 0, assists = 0, deaths = 0, damage = 0 } = player ?? {};
  // Calculations
  const kpr = calcRate(kills, roundsLength, 1);
  const kda = calcRate(kills + assists, deaths);
  const dpr = calcRate(damage, roundsLength, 0);
  return {
    kpr,
    kda,
    dpr,
  };
};

export const getProfileAvatar = () => {
  // Only this default exists AFAIK, ideally we could get a steam profile avatar
  return `${appURLs.CDN}/blitz/cs2/cs2-profile-avatar-default.webp`;
};

export const getRankIcon = (iconName: string) => {
  return `${appURLs.CDN}/blitz/csgo/ranks_large/${iconName}.webp`;
};

export const getRankIconSmall = (iconName: string) => {
  return `${appURLs.CDN}/blitz/csgo/ranks/${iconName}.webp`;
};

export const getWeaponIcon = (iconName: string) => {
  return `${appURLs.CDN}/blitz/csgo/weapons/${iconName}.webp`;
};

export const getGameModeIcon = (gameModeName: string) => {
  return `${appURLs.CDN}/blitz/csgo/gamemode/${gameModeName}.webp`;
};

export const getPlayerIcon = (
  gameType: number,
  rank: number,
  teamId: number,
) => {
  const iconName = (RANKS[gameType] ?? RANKS[GAME_MODE_RANKS.COMPETITIVE])?.[
    rank
  ]?.icon;
  if (iconName && iconName !== "none") return getRankIconSmall(iconName);
  if (teamId === TEAM_IDS.T) return CSGOTerroristIcon;
  return CSGOCounterTerroristIcon;
};

export const isMatchAbandoned = (
  match: DeepReadonly<MatchStart | MatchFull>,
) => {
  const gameMode = GAME_MODES?.[`${match.gameType}_${match.gameMode}`];

  if (!gameMode) {
    appWarn("[CS2] Unable to find game mode", {
      gameModeKey: `${match.gameType}_${match.gameMode}`,
    });
    return false;
  }

  const rounds = cs2Utils.getRoundsLength(match?.rounds);
  // Simple math to determine if the match is abandoned or not
  // You need a minimum of the max rounds / 2 (rounded down) to win a game if you destroyed
  // the other team, if it exceeds that, then its either overtime (from draw) or a team
  // that didn't get stomped like a bunch of noobs
  return !(rounds >= Math.floor(gameMode.maxRounds / 2));
};

export const isArmsRace = (match: DeepReadonly<MatchStart | MatchFull>) => {
  return match?.gameType === 1 && match?.gameMode === 0;
};

// this includes Casual, Competitive and Wingman
export const isBombDefusalMode = (
  match: DeepReadonly<MatchStart | MatchFull>,
) => {
  return match?.gameType === 0;
};

const RegExpSteamDigits = /(\d):(\d+)$/;

/**
 * Creates a deterministic UUID-like match ID using the same payload approach as createSHA2MatchId.
 * The UUID format is: xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx
 * where x is any hex digit and y is one of 8, 9, A, or B (version 4 UUID format).
 *
 * This function is pure and does not modify the input match object in any way.
 *
 * @param match - The match data to create the ID from
 * @returns A UUID-like string that's deterministic based on match data
 */
export const createUUIDLikeMatchId = async (
  match: DeepReadonly<MatchStart | MatchFull>,
): Promise<string | undefined> => {
  const latestRound = cs2Utils.getLastRound(match);
  // Should be impossible case since this fn should never be called at all
  // without a complete match with rounds played, but this is here just incase
  if (!latestRound) return;

  // Prepare steam id for hashing (same logic as createSHA2MatchId)
  // .map() already creates a new array of string values, so no need for .slice()
  const steamIds = latestRound.playerMatchRoundStats
    .filter((i) => i.steamId !== BOT_STEAM_ID)
    .map((i) => i.steamId)
    .sort((a, b) => {
      const steamGenAndId_1 = a.match(RegExpSteamDigits);
      const steamGenAndId_2 = b.match(RegExpSteamDigits);
      const steamGenAndId_a = Number(
        steamGenAndId_1?.length ? steamGenAndId_1.slice(1, 3).join("") : a,
      );
      const steamGenAndId_b = Number(
        steamGenAndId_2?.length ? steamGenAndId_2.slice(1, 3).join("") : b,
      );
      if (steamGenAndId_a < steamGenAndId_b) return -1;
      if (
        steamGenAndId_a === steamGenAndId_b &&
        steamGenAndId_1[1] < steamGenAndId_2[1]
      )
        return -1;
      return 1;
    });

  // Create more uniqueness for this match (same payload as createSHA2MatchId)
  const { terroristCurrentScore, counterTerroristCurrentScore } = latestRound;
  const maxRounds =
    GAME_MODES[`${match.gameType}_${match.gameMode}`].maxRounds ?? 0;

  const payload = [
    steamIds.join(""),
    terroristCurrentScore,
    counterTerroristCurrentScore,
    match.gameType,
    match.gameMode,
    maxRounds,
    match.map,
  ];

  const input = payload.join("");

  // Generate SHA-256 hash
  const msgBuffer = new TextEncoder().encode(input);
  const hashBuffer = await crypto.subtle.digest("SHA-256", msgBuffer);
  const hashArray = Array.from(new Uint8Array(hashBuffer));

  // Convert to hex string (we need 32 hex characters for UUID)
  const hashHex = hashArray
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("")
    .substring(0, 32); // Take first 32 hex chars (128 bits)

  // Format as UUID: xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx
  const uuid = [
    hashHex.substring(0, 8), // 8 hex chars
    hashHex.substring(8, 12), // 4 hex chars
    "4" + hashHex.substring(13, 16), // Version 4: starts with '4' + 3 hex chars
    ((parseInt(hashHex.substring(16, 17), 16) & 0x3) | 0x8).toString(16) +
      hashHex.substring(17, 20), // Variant bits + 3 hex chars
    hashHex.substring(20, 32), // 12 hex chars
  ].join("-");

  return uuid;
};
